import React, { Component } from 'react'
import ResourceMap from './ResourceMap'
import ResourceCard from './ResourceCard'
import Loader from './Loader'
import axios from 'axios';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import FormControl from 'react-bootstrap/FormControl';
import ReactToPrint from 'react-to-print';
import 'react-widgets/dist/css/react-widgets.css';
import PropTypes from 'prop-types';
import { Popover, OverlayTrigger, InputGroup, Dropdown } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { translate } from 'react-switch-lang';
import TranslateRegionCard from "./Filter/RegionCard";
import ServiceFilterControls from "./ServiceFilterControls";
import {
  SERVICE_CATEGORY_KEYS,
  filterFacilitiesBySelection,
  getDefaultServiceSelection,
  getServiceTypeDisplayMode,
  getVisibleServiceTypes,
} from "./serviceFilterModel";

// Service Map tab: loads services + facilities from separate API routes; normalizes coords to $numberDouble for ResourceMap (isResource=false).
// Reuses Filter region UI patterns; category UI is ServiceCategoryCard below.

const SERVICE_REGION_KEYS = [
  "CENTRAL WESTERN, SOUTHERN AND ISLANDS",
  "EASTERN AND WAN CHAI",
  "KOWLOON CITY & YAU TSIM MONG",
  "WONG TAI SIN AND SAI KUNG",
  "SHAM SHUI PO",
  "SHA TIN",
  "TAI PO AND NORTH",
  "YUEN LONG",
  "TSUEN WAN AND KWAI TSING",
  "TUEN MUN",
  "KWUN TONG",
];

const SERVICE_MAP_SOURCE_URL =
  "https://www.swd.gov.hk/en/svcdesk/download/listofserv/";

// Service-specific CategoryCard using the same dropdown style as Filter.js
function ServiceCategoryCard({
  t,
  categoryValue,
  categories,
  open,
  setOpen,
  onCardChange,
  dropdownDirection,
}) {
  const toggleItem = (item) => {
    const isSelected = categoryValue.includes(item);
    const updated = isSelected
      ? categoryValue.filter((i) => i !== item)
      : [...categoryValue, item];
    onCardChange(updated);
  };

  const categoryKeys =
    Array.isArray(categories) && categories.length > 0
      ? categories
      : SERVICE_CATEGORY_KEYS;

  const renderTitle = () => {
    const serviceUnitLabel = t("serviceMap.serviceUnitLabel");
    if (categoryValue.length === 0) {
      return `🏷️ ${serviceUnitLabel}: ${t("serviceMap.serviceUnitPlaceholder")}`;
    }
    if (categoryValue.length === 1) {
      return `🏷️ ${serviceUnitLabel}: ${t(`categories.${categoryValue[0]}`)}`;
    }
    return `🏷️ ${serviceUnitLabel}: ${t("common.Selected")} ${categoryValue.length}`;
  };

  return (
    <Dropdown
      drop={dropdownDirection}
      show={open}
      onToggle={(isOpen) => setOpen(isOpen)}
      className="filter-pill w-100"
    >
      <Dropdown.Toggle
        variant="light"
        className="dropdown-toggle-pill w-100 text-start"
      >
        {renderTitle()}
      </Dropdown.Toggle>

      <Dropdown.Menu renderOnMount>
        {categoryKeys.map((cat, idx) => (
          <Dropdown.Item
            key={idx}
            as="button"
            className="d-flex align-items-center"
            onClick={() => {
              toggleItem(cat);
            }}
          >
            <input
              type="checkbox"
              className="me-2 mr-2"
              readOnly
              checked={categoryValue.includes(cat)}
            />
            {t(`categories.${cat}`)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

ServiceCategoryCard.propTypes = {
  t: PropTypes.func.isRequired,
  categoryValue: PropTypes.array.isRequired,
  categories: PropTypes.array,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCardChange: PropTypes.func.isRequired,
  dropdownDirection: PropTypes.string,
};

ServiceCategoryCard.defaultProps = {
  categories: SERVICE_CATEGORY_KEYS,
  dropdownDirection: "down",
};

const TranslateCategoryCard = translate(ServiceCategoryCard);
export { TranslateCategoryCard };


// FacilityCard using the same dropdown style as Filter.js
function FacilityCard({
  t,
  facilitiesData,
  facilitiesValue,
  onCardChange,
  dropdownDirection,
  alignRight,
}) {
  const [facilityOpen, setFacilityOpen] = React.useState(false);

  const toggleItem = (item) => {
    const facilityTitle = t('facilities.Title');
    const itemValue = item[facilityTitle] || item.Facility || item;
    const isSelected = facilitiesValue.some(f => {
      const fValue = f[facilityTitle] || f.Facility || f;
      return fValue === itemValue;
    });
    const updated = isSelected
      ? facilitiesValue.filter((f) => {
          const fValue = f[facilityTitle] || f.Facility || f;
          return fValue !== itemValue;
        })
      : [...facilitiesValue, item];
    onCardChange(updated);
  };

  const renderTitle = () => {
    if (facilitiesValue.length === 0) return `🏢 ${t('facilities.Placeholder')}`;
    if (facilitiesValue.length === 1) {
      const facilityTitle = t('facilities.Title');
      const name =
        facilitiesValue[0][facilityTitle] ||
        facilitiesValue[0].Facility ||
        facilitiesValue[0];
      return `🏢 ${facilityTitle}: ${name}`;
    }
    return `🏢 ${t("facilities.Title")}: ${t("common.Selected")} ${facilitiesValue.length}`;
  };

  const facilityTitle = t('facilities.Title');

  return (
    <Dropdown
      drop={dropdownDirection !== "down" ? "up" : "down"}
      show={facilityOpen}
      onToggle={(isOpen) => setFacilityOpen(isOpen)}
      className="filter-pill w-100"
    >
      <Dropdown.Toggle
        variant="light"
        className="dropdown-toggle-pill w-100 text-start"
      >
        {renderTitle()}
      </Dropdown.Toggle>

      <Dropdown.Menu alignRight={alignRight} renderOnMount>
        {facilitiesData.map((facility, idx) => {
          const facilityName = facility[facilityTitle] || facility.Facility || facility;
          const isSelected = facilitiesValue.some(f => {
            const fValue = f[facilityTitle] || f.Facility || f;
            return fValue === facilityName;
          });
          return (
            <Dropdown.Item
              key={idx}
              as="button"
              className="d-flex align-items-center"
              onClick={() => {
                toggleItem(facility);
              }}
            >
              <input
                type="checkbox"
                className="me-2 mr-2"
                readOnly
                checked={isSelected}
              />
              {facilityName}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

FacilityCard.propTypes = {
  t: PropTypes.func.isRequired,
  facilitiesData: PropTypes.array.isRequired,
  facilitiesValue: PropTypes.array.isRequired,
  onCardChange: PropTypes.func.isRequired,
  dropdownDirection: PropTypes.string,
  alignRight: PropTypes.bool,
};

const TranslateFacilityCard = translate(FacilityCard);
export { TranslateFacilityCard };


class ServiceFilterForm extends Component {

	constructor(props) {
		super(props)
		this.closePopover = this.closePopover.bind(this);
		this._isMounted = false;
		const defaultSelection = getDefaultServiceSelection();
		this.state = {
			popoverOpen: false,
			showWelcomeModal: false, // Will be set to true after data loads
			loading:true,
			loadingServices: false,
			hasLoadedServices: false,
		  searchValue: "",
		  locations:[],
		  filteredLocations:[],
		  regions: SERVICE_REGION_KEYS,
		  regionValue:[],
		  categories: SERVICE_CATEGORY_KEYS,
		  facilities:['all'],
		  facilitiesValue:[], //to put on the ui for multiselect
		  facilitiesData:[],
		  facilitiesOriginal:[],
		  serviceTypesData: [],
		  serviceTypeValue: defaultSelection.serviceTypeKeys,
		  categoryValue: defaultSelection.categoryKeys, //to put on the ui for multiselect
		  currentRegion: 'all',
		  currentCategory: defaultSelection.categoryKeys,
		  currentServiceType: defaultSelection.serviceTypeKeys,
		  currentFacility: [],
		  lat: 22.29442, //default lat
      lng: 114.17274, //default long
      zoom:10, //default zoom
      tooltip:'true',
      navExpanded: true,
      open: false,
      regionOpen: false,
		}
	}


	async componentDidUpdate(prevProps) {
		//parentnavExpanded so we can do interaction when click filter at the top bar (apps.js) it will close the filter at the bottom bar
		const parentNavExpanded = this.props.navExpanded
		//pass lang so can clear filter when language change
		const lang = this.props.lang
		if(prevProps.navExpanded != parentNavExpanded) {
			this.setNavExpanded(false)
			this.closePopover();
		}
		//so when language is changed
		if(prevProps.lang != lang){
			this.clearFilter()
			this.openPopover();
		}
	}

	calculateMidpoint = (locations) => {
		// Default coordinates (Hong Kong)
		const defaultLat = 22.29442;
		const defaultLng = 114.17274;
		const defaultZoom = 10;
		
		//calculate midpoint based on filtered locations
		if (locations.length == 0) {
			// if empty no location then default
			return { avgLat: defaultLat, avgLong: defaultLng, zoom: defaultZoom };
		} else if (locations.length == 1) {
			// Single location - zoom in close but not too close
			const location = locations[0];
			// Handle both nested and direct latitude/longitude structures
			const latStr = location["Latitude"]?.$numberDouble || location["Latitude"] || location["latitude"];
			const lngStr = location["Longitude"]?.$numberDouble || location["Longitude"] || location["longitude"];
			
			// Parse and validate coordinates
			const lat = parseFloat(latStr);
			const lng = parseFloat(lngStr);
			
			// Use defaults if coordinates are invalid
			const avgLat = (!isNaN(lat) && isFinite(lat)) ? lat : defaultLat;
			const avgLong = (!isNaN(lng) && isFinite(lng)) ? lng : defaultLng;
			const zoom = 14; // Good zoom for single location
			
			if (!this.props.both) {
				// Adjust for mobile filter
				return { avgLat: avgLat - 0.01, avgLong, zoom };
			}
			return { avgLat, avgLong, zoom };
		} else {
			//find the four points between all locations
			// Handle both nested and direct latitude/longitude structures
			const firstLatStr = locations[0]["Latitude"]?.$numberDouble || locations[0]["Latitude"] || locations[0]["latitude"];
			const firstLngStr = locations[0]["Longitude"]?.$numberDouble || locations[0]["Longitude"] || locations[0]["longitude"];
			
			// Parse and validate first location
			const firstLat = parseFloat(firstLatStr);
			const firstLng = parseFloat(firstLngStr);
			
			// If first location is invalid, use defaults
			if (isNaN(firstLat) || isNaN(firstLng) || !isFinite(firstLat) || !isFinite(firstLng)) {
				console.warn(`⚠️ calculateMidpoint: First location has invalid coordinates, using defaults`);
				return { avgLat: defaultLat, avgLong: defaultLng, zoom: defaultZoom };
			}
			
			var minLat = firstLat;
			var maxLat = firstLat;
			var minLong = firstLng;
			var maxLong = firstLng;
			
			locations.forEach((location) => {
				const latStr = location["Latitude"]?.$numberDouble || location["Latitude"] || location["latitude"];
				const lngStr = location["Longitude"]?.$numberDouble || location["Longitude"] || location["longitude"];
				
				// Parse and validate coordinates
				const lat = parseFloat(latStr);
				const lng = parseFloat(lngStr);
				
				// Only use valid coordinates for bounds calculation
				if (!isNaN(lat) && isFinite(lat) && !isNaN(lng) && isFinite(lng)) {
					if (lat > maxLat) {
						maxLat = lat;
					}
					if (lat < minLat) {
						minLat = lat;
					}
					if (lng > maxLong) {
						maxLong = lng;
					}
					if (lng < minLong) {
						minLong = lng;
					}
				}
			});
			
			// Add padding around the bounds to ensure all markers are visible
			const padding = 0.002; // Reduced padding for tighter zoom
			minLat = minLat - padding;
			maxLat = maxLat + padding;
			minLong = minLong - padding;
			maxLong = maxLong + padding;
			
			//find avg points
			var avgLat = (minLat + maxLat) / 2;
			var avgLong = (minLong + maxLong) / 2;
			const diffLat = maxLat - minLat;
			const diffLong = maxLong - minLong;
			
			// Validate calculated midpoint
			if (isNaN(avgLat) || isNaN(avgLong) || !isFinite(avgLat) || !isFinite(avgLong)) {
				console.warn(`⚠️ calculateMidpoint: Calculated midpoint is invalid, using defaults`);
				return { avgLat: defaultLat, avgLong: defaultLng, zoom: defaultZoom };
			}
			
			// Calculate zoom based on the spread of locations
			// Higher zoom = more zoomed in, lower zoom = more zoomed out
			let zoom = 12; // Default zoom
			const maxDiff = Math.max(diffLat, diffLong);
			
			// Dynamic zoom calculation based on spread
			if (maxDiff >= 0.1) {
				zoom = 12; // Very wide spread - zoom out to see all
			} else if (maxDiff >= 0.05) {
				zoom = 13; // Wide spread
			} else if (maxDiff >= 0.03) {
				zoom = 13.5; // Medium spread
			} else if (maxDiff >= 0.01) {
				zoom = 13.8; // Close spread - zoom in more
			} else if (maxDiff >= 0.005) {
				zoom = 14; // Very close spread - zoom in even more
			} else {
				zoom = 15; // Extremely close spread - maximum zoom
			}
			
			// Adjust for mobile vs desktop
			if (!this.props.both) {
				// Mobile adjustments
				if (diffLat >= 0.05) {
					avgLat = avgLat - 0.06;
				} else if (diffLat >= 0.03) {
					avgLat = avgLat - 0.03;
				} else if (diffLat >= 0.01) {
					avgLat = avgLat - 0.015;
				} else if (diffLat >= 0.005) {
					avgLat = avgLat - 0.01;
				} else {
					avgLat = avgLat - 0.0075;
				}
			}
			
			console.log(`Auto-zoom: ${locations.length} locations, zoom: ${zoom}, center: [${avgLat}, ${avgLong}]`);
			return { avgLat, avgLong, zoom };
		}
	};

	clearFilter = () => {
		const defaultSelection = getDefaultServiceSelection();
		//clears the filter
		this.setState({
			currentRegion: "all",
			currentCategory: defaultSelection.categoryKeys,
			currentServiceType: defaultSelection.serviceTypeKeys,
			currentFacility: [],
			regionValue: [],
			categoryValue: defaultSelection.categoryKeys,
			categories: SERVICE_CATEGORY_KEYS,
			serviceTypeValue: defaultSelection.serviceTypeKeys,
			serviceTypesData: getVisibleServiceTypes(
				this.state.facilitiesOriginal,
				defaultSelection.categoryKeys
			),
			facilitiesValue: [],
			facilitiesData: filterFacilitiesBySelection(
				this.state.facilitiesOriginal,
				defaultSelection.categoryKeys,
				defaultSelection.serviceTypeKeys
			),
			searchValue: "",
			lat: 22.29442,
			lng: 114.17274,
			zoom: 10,
			filteredLocations: this.state.locations,
		}, () => {
			this.filterLocations();
		});
	}

	filterLocations = (sourceLocations = this.state.locations) => {
		// main function for filtering locations
		const { t } = this.props;
		let filteredLocations = sourceLocations;
		const categoryKeys = Array.isArray(this.state.currentCategory)
			? this.state.currentCategory
			: [];
		const serviceTypeDisplayMode = getServiceTypeDisplayMode(categoryKeys);
		const serviceTypeKeys =
			serviceTypeDisplayMode === "enabled" ? this.state.currentServiceType : [];
		const region = this.state.currentRegion;
		const facilities = this.state.currentFacility.map((item) =>
			item.Facility || item[t('facilities.Title')] || item
		);
		const searchValue = this.state.searchValue;
		const facilitiesData = filterFacilitiesBySelection(
			this.state.facilitiesOriginal,
			categoryKeys,
			serviceTypeKeys
		);
		const serviceTypesData = serviceTypeDisplayMode === "enabled"
			? getVisibleServiceTypes(this.state.facilitiesOriginal, categoryKeys)
			: [];

		if (region != "all" && region.length > 0) {
			//filter based on region
			filteredLocations = filteredLocations.filter((d, index) => {
				// Always use the raw "District" field for filtering to avoid language mismatch.
				const locationRegion = d["District"];

				// If the location has no region data, exclude it.
				if (!locationRegion) {
					return false;
				}

				// Check if locationRegion is a string before calling toLowerCase
				if (typeof locationRegion !== 'string') {
					console.error(`ERROR: Location ${index} District is not a string:`, typeof locationRegion, locationRegion);
					return false;
				}

				try {
					// Normalize both the location's region and the selected regions for a case-insensitive comparison.
					const normalizedLocationRegion = locationRegion.toLowerCase().trim();
					const normalizedSelectedRegions = region.map((r) => {
						if (typeof r !== 'string') {
							console.error(`ERROR: Region filter value is not a string:`, typeof r, r);
							return '';
						}
						return r.toLowerCase().trim();
					});

					const shouldInclude =
						normalizedSelectedRegions.includes(normalizedLocationRegion);

					return shouldInclude;
				} catch (error) {
					console.error(`ERROR in toLowerCase for location ${index}:`, error, locationRegion);
					return false;
				}
			});
			let { avgLat, avgLong, zoom } = this.calculateMidpoint(filteredLocations);
			this.setState({
				zoom: zoom,
				lat: avgLat,
				lng: avgLong,
			});
		} else {
			//change back to default if all

			this.setState({
				zoom: 10.5,
				lat: 22.29442,
				lng: 114.17274,
			});
		}
		if (categoryKeys !== "all" && categoryKeys.length > 0) {
			//filter based on category
			filteredLocations = filteredLocations.filter((d) => {
				const locationCategory = d.Category || d[t("categories.Title")];
				return categoryKeys.includes(locationCategory);
			});
			let { avgLat, avgLong, zoom } = this.calculateMidpoint(filteredLocations);
			this.setState({
				zoom: zoom,
				lat: avgLat,
				lng: avgLong,
			});
		}

		if (serviceTypeKeys.length > 0) {
			filteredLocations = filteredLocations.filter((d) =>
				serviceTypeKeys.includes(d["Type of Service"])
			);
			let { avgLat, avgLong, zoom } = this.calculateMidpoint(filteredLocations);
			this.setState({
				zoom: zoom,
				lat: avgLat,
				lng: avgLong,
			});
		}

		if (facilities.length > 0 && facilities[0] !== undefined && facilities[0] !== 'all') {
			//filter for facilities
			filteredLocations = filteredLocations.filter((d) => {
				const locationFacility = d.Facility || d[t('facilities.Title')];
				const shouldInclude = facilities.includes(locationFacility);
				return shouldInclude;
			});
			let { avgLat, avgLong, zoom } = this.calculateMidpoint(filteredLocations);
			this.setState({
				zoom: zoom,
				lat: avgLat,
				lng: avgLong,
			});
		}
		if (searchValue !== "") {
			//filter for search values
			filteredLocations = filteredLocations.filter((d, index) => {
				try {
					// Check each field before calling toLowerCase
					const nameField =
						d.Name ||
						d[t("columns.ServiceName")] ||
						d[t("columns.Name")];
					const facilityField = d.Facility || d[t("columns.Facility")];
					
					// Check if fields are strings before calling toLowerCase
					const nameMatch = nameField && typeof nameField === 'string' ? 
						nameField.toLowerCase().includes(searchValue.toLowerCase()) : false;
					const facilityMatch = facilityField && typeof facilityField === 'string' ? 
						facilityField.toLowerCase().includes(searchValue.toLowerCase()) : false;
					
					const result = nameMatch || facilityMatch;
					return result;
				} catch (error) {
					console.error(`ERROR in search filter for location ${index}:`, error, d);
					return false;
				}
			});
		}
		//set state so will render and filter
		this.setState({
			filteredLocations: filteredLocations,
			facilitiesData: facilitiesData,
			serviceTypesData: serviceTypesData,
		});
	};

	loadServices = async () => {
		const res = await axios.get(
			"https://resource-project-backend.vercel.app/api/services",
			{
				params: {
					$limit: 500,
				},
			}
		);

		// Handle services data structure - check both possible formats
		let locations = Array.isArray(res.data.data) ? res.data.data :
		                Array.isArray(res.data) ? res.data : [];

		// Validate that we have data
		if (!Array.isArray(locations) || locations.length === 0) {
			console.error("ERROR: No valid locations data received", res.data);
			return [];
		}

		// Store original count for logging
		const originalCount = locations.length;
		
		// Normalize the data structure and filter out locations without valid coordinates
		// ResourceMap expects services to have: Latitude['$numberDouble'] and Longitude['$numberDouble']
		locations = locations.map((location) => {
				const normalizedLocation = { ...location };
				
				// Normalize Latitude - handle both number and nested format
				if (normalizedLocation.Latitude !== undefined && normalizedLocation.Latitude !== null) {
					if (typeof normalizedLocation.Latitude === 'number') {
						// Check if it's a valid number (not NaN) before converting
						if (!isNaN(normalizedLocation.Latitude) && isFinite(normalizedLocation.Latitude)) {
							normalizedLocation.Latitude = { $numberDouble: normalizedLocation.Latitude.toString() };
						} else {
							// Invalid number, set to undefined so it gets filtered out
							normalizedLocation.Latitude = undefined;
						}
					} else if (normalizedLocation.Latitude.$numberDouble === undefined) {
						// Try to parse if it's a string or other format
						const latNum = parseFloat(normalizedLocation.Latitude);
						if (!isNaN(latNum) && isFinite(latNum)) {
							normalizedLocation.Latitude = { $numberDouble: latNum.toString() };
						} else {
							// Invalid number, set to undefined so it gets filtered out
							normalizedLocation.Latitude = undefined;
						}
					} else {
						// Already in nested format, validate it
						const latNum = parseFloat(normalizedLocation.Latitude.$numberDouble);
						if (isNaN(latNum) || !isFinite(latNum) || normalizedLocation.Latitude.$numberDouble === "NaN") {
							normalizedLocation.Latitude = undefined;
						}
					}
				}
				
				// Normalize Longitude - handle both number and nested format
				if (normalizedLocation.Longitude !== undefined && normalizedLocation.Longitude !== null) {
					if (typeof normalizedLocation.Longitude === 'number') {
						// Check if it's a valid number (not NaN) before converting
						if (!isNaN(normalizedLocation.Longitude) && isFinite(normalizedLocation.Longitude)) {
							normalizedLocation.Longitude = { $numberDouble: normalizedLocation.Longitude.toString() };
						} else {
							// Invalid number, set to undefined so it gets filtered out
							normalizedLocation.Longitude = undefined;
						}
					} else if (normalizedLocation.Longitude.$numberDouble === undefined) {
						// Try to parse if it's a string or other format
						const lngNum = parseFloat(normalizedLocation.Longitude);
						if (!isNaN(lngNum) && isFinite(lngNum)) {
							normalizedLocation.Longitude = { $numberDouble: lngNum.toString() };
						} else {
							// Invalid number, set to undefined so it gets filtered out
							normalizedLocation.Longitude = undefined;
						}
					} else {
						// Already in nested format, validate it
						const lngNum = parseFloat(normalizedLocation.Longitude.$numberDouble);
						if (isNaN(lngNum) || !isFinite(lngNum) || normalizedLocation.Longitude.$numberDouble === "NaN") {
							normalizedLocation.Longitude = undefined;
						}
					}
				}
				
				// Normalize index if needed
				if (normalizedLocation.index !== undefined && typeof normalizedLocation.index === 'number') {
					normalizedLocation.index = { $numberInt: normalizedLocation.index.toString() };
				}
				
				return normalizedLocation;
		}).filter((location, originalIndex) => {
				// Filter out locations with missing or invalid coordinates
				// Both Latitude and Longitude must exist and be valid numbers
				const lat = location.Latitude?.$numberDouble;
				const lng = location.Longitude?.$numberDouble;
				
				// Get location identifier for logging
				const locationName = location.Name || location["Name"] || location["機構名稱"] || location["機構名稱"] || "Unknown";
				const locationIndex = location.index?.$numberInt || location.index || originalIndex;
				const locationId = location._id || location.GMID || "Unknown";
				
				// Check for missing coordinates
				if (!lat || !lng || lat === "" || lng === "" || lat === "undefined" || lng === "undefined") {
					console.error(`❌ INVALID COORDINATES - Missing lat/lng:`, {
						index: locationIndex,
						name: locationName,
						id: locationId,
						Latitude: location.Latitude,
						Longitude: location.Longitude,
						latValue: lat,
						lngValue: lng,
						fullLocation: location
					});
					return false; // Missing coordinates
				}
				
				const latNum = parseFloat(lat);
				const lngNum = parseFloat(lng);
				
				// Check for invalid numbers (NaN, Infinity, or string "NaN")
				if (isNaN(latNum) || isNaN(lngNum) || !isFinite(latNum) || !isFinite(lngNum) || lat === "NaN" || lng === "NaN" || lat === "Infinity" || lng === "Infinity") {
					console.error(`❌ INVALID COORDINATES - Not a number:`, {
						index: locationIndex,
						name: locationName,
						id: locationId,
						Latitude: location.Latitude,
						Longitude: location.Longitude,
						latValue: lat,
						lngValue: lng,
						latParsed: latNum,
						lngParsed: lngNum,
						fullLocation: location
					});
					return false; // Invalid numbers
				}
				
				// Check if coordinates are within valid ranges
				if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) {
					console.error(`❌ INVALID COORDINATES - Out of range:`, {
						index: locationIndex,
						name: locationName,
						id: locationId,
						Latitude: location.Latitude,
						Longitude: location.Longitude,
						latValue: lat,
						lngValue: lng,
						latParsed: latNum,
						lngParsed: lngNum,
						latRange: latNum < -90 || latNum > 90 ? "OUT OF RANGE" : "OK",
						lngRange: lngNum < -180 || lngNum > 180 ? "OUT OF RANGE" : "OK",
						fullLocation: location
					});
					return false; // Out of valid range
				}
				
				return true;
		});
		
		// Log how many locations were filtered out
		const filteredCount = originalCount - locations.length;
		if (filteredCount > 0) {
			console.warn(`⚠️ Filtered out ${filteredCount} location(s) without valid coordinates`);
			console.warn(`📊 Original count: ${originalCount}, Valid count: ${locations.length}`);
			console.warn(`🔍 Check the console errors above (search for "INVALID COORDINATES") to see which data points have invalid lat/lng`);
		}
		
		// Double-check all remaining locations have valid coordinates before setting state
		const invalidLocations = locations.filter((location) => {
				const lat = location.Latitude?.$numberDouble;
				const lng = location.Longitude?.$numberDouble;
				const latNum = parseFloat(lat);
				const lngNum = parseFloat(lng);
				return !lat || !lng || isNaN(latNum) || isNaN(lngNum) || 
				       latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180;
		});
		
		if (invalidLocations.length > 0) {
			console.error(`🚨 CRITICAL: Found ${invalidLocations.length} location(s) with invalid coordinates that passed initial filter!`, invalidLocations);
			// Filter them out again
			locations = locations.filter((location) => {
					const lat = location.Latitude?.$numberDouble;
					const lng = location.Longitude?.$numberDouble;
					const latNum = parseFloat(lat);
					const lngNum = parseFloat(lng);
					return lat && lng && !isNaN(latNum) && !isNaN(lngNum) && 
					       latNum >= -90 && latNum <= 90 && lngNum >= -180 && lngNum <= 180;
			});
		}

		// Final validation: Check all locations one more time before setting state
		locations.forEach((location, index) => {
				const lat = location.Latitude?.$numberDouble;
				const lng = location.Longitude?.$numberDouble;
				if (!lat || !lng) {
					const locationName = location.Name || location["Name"] || location["機構名稱"] || "Unknown";
					const locationIndex = location.index?.$numberInt || location.index || index;
					console.error(`🚨 FINAL CHECK FAILED - Location still has invalid coordinates:`, {
						index: locationIndex,
						name: locationName,
						Latitude: location.Latitude,
						Longitude: location.Longitude,
						latValue: lat,
						lngValue: lng
					});
				}
		});

		// Check for critical data issues
		console.log("✅ Loaded locations data - count:", locations.length);
		
		// Check for problematic data in the first few locations
		if (locations.length > 0) {
			locations.slice(0, 3).forEach((location, index) => {
				if (location.Category === undefined || location.Category === null) {
					console.error(`ERROR: Location ${index} has undefined/null Category:`, location);
				}
				if (location.District === undefined || location.District === null) {
					console.error(`ERROR: Location ${index} has undefined/null District:`, location);
				}
				if (typeof location.District !== 'string' && location.District !== undefined) {
					console.error(`ERROR: Location ${index} District is not a string:`, typeof location.District, location.District);
				}
			});
		}

		return locations;
	}

	ensureServiceLocationsLoaded = async () => {
		if (this.state.hasLoadedServices) {
			return { loaded: true, locations: this.state.locations };
		}

		if (this.state.loadingServices) {
			return { loaded: false, locations: this.state.locations };
		}

		if (this._isMounted) {
			this.setState({ loadingServices: true });
		}

		try {
			const locations = await this.loadServices();
			if (this._isMounted) {
				this.setState({
					loadingServices: false,
					hasLoadedServices: true,
					locations: locations,
				});
			}
			return { loaded: true, locations };
		} catch (error) {
			console.error("ERROR loading services:", error);
			if (this._isMounted) {
				this.setState({
					loadingServices: false,
					hasLoadedServices: false,
					locations: [],
					filteredLocations: [],
				});
			}
			return { loaded: false, locations: [] };
		}
	}

	async componentDidMount() {
		this._isMounted = true;
		try {
			const defaultSelection = getDefaultServiceSelection();
			window.gtag("event", "page_view", {
				page_path: "/servicemap",
			});

			const res = await axios.get(
				"https://resource-project-backend.vercel.app/api/facilities",
				{
					params: {
						$limit: 500,
					},
				}
			);

			const facilitiesOriginal = Array.isArray(res.data.data) ? res.data.data :
			                          Array.isArray(res.data) ? res.data : [];
			const facilitiesData = filterFacilitiesBySelection(
				facilitiesOriginal,
				defaultSelection.categoryKeys,
				defaultSelection.serviceTypeKeys
			);
			const serviceTypesData = getVisibleServiceTypes(
				facilitiesOriginal,
				defaultSelection.categoryKeys
			);

			if (this._isMounted) {
				this.setState({
					loading: false,
					locations: [],
					filteredLocations: [],
					regions: SERVICE_REGION_KEYS,
					categories: SERVICE_CATEGORY_KEYS,
					facilitiesData: facilitiesData,
					facilitiesOriginal: facilitiesOriginal,
					serviceTypesData: serviceTypesData,
					showWelcomeModal: true,
					popoverOpen: false,
				});
			}
		} catch (error) {
			console.error("ERROR loading facilities:", error);
			if (this._isMounted) {
				this.setState({
					loading: false,
					locations: [],
					filteredLocations: [],
					regions: SERVICE_REGION_KEYS,
					categories: SERVICE_CATEGORY_KEYS,
					facilitiesData: [],
					facilitiesOriginal: [],
					serviceTypesData: [],
				});
			}
		}
	}

	componentWillUnmount() {
		this._isMounted = false;
	}

	setNavExpanded = (expanded) => {
		this.setState({ navExpanded: expanded });
		this.closePopover();
	}

	doNothing = () => {}

	//setOpen is for turning the category dropdown off
	setOpen = () => {
		this.setState((prevState) => ({
			open: !prevState.open,
		}));
	}

	setRegionOpen = (isOpen) => {
		this.setState({
			regionOpen: isOpen,
		});
	}

	//close the filter
	closeNav = () => {
		this.setState({ navExpanded: false });
		this.closePopover();
	}
	
	closePopover() {
		this.setState({
			popoverOpen: false,
		});
	}

	openPopover() {
		this.setState({
			popoverOpen: true,
		});
	}

	// Welcome modal handlers
	handleWelcomeModalOkay = async () => {
		// Load the heavy services dataset only after the user confirms the filter modal.
		const { loaded, locations } = await this.ensureServiceLocationsLoaded();
		if (!loaded || !this._isMounted) {
			return;
		}

		this.filterLocations(locations);
		this.closeWelcomeModal();
	};

	closeWelcomeModal = () => {
		this.setState({
			showWelcomeModal: false,
			popoverOpen: true, // Re-enable popover after welcome modal closes
		});
	};

	handleOrgSearch = (e) => {
		this.setState({searchValue: e.target.value}, () => {this.filterLocations()})
	}

	handleRegionCardChange = (region) => {
		this.setState(
			{
				currentRegion: region,
				regionValue: region,
			},
			() => {
				this.filterLocations();
				this.setRegionOpen(false); // Close the dropdown
			}
		);
	};


	handleCategoryCardChange = (cat) => {
		this.setState(
			{
				currentCategory: cat,
				categoryValue: cat,
				currentServiceType: [],
				serviceTypeValue: [],
				currentFacility: [],
				facilitiesValue: [],
			},
			() => {
				this.filterLocations();
				this.setOpen();
			}
		);
	};

	handleServiceTypeChange = (serviceTypes) => {
		this.setState(
			{
				currentServiceType: serviceTypes,
				serviceTypeValue: serviceTypes,
				currentFacility: [],
				facilitiesValue: [],
			},
			() => {
				this.filterLocations();
			}
		);
	};

	handleFacilityCardChange = (fac) => {
		this.setState({
			currentFacility: fac,
			facilitiesValue: fac
		}, () => {
			this.filterLocations();
		});
	}
	myCallback = (dataFromChild) => {
        this.setState({ lat: dataFromChild.lat, lng: dataFromChild.lng });
    }

	renderServiceFilterControls = (
		dropdownDirection = "down",
		layout = "toolbar"
	) => (
		<ServiceFilterControls
			t={this.props.t}
			regionProps={{
				regionValue: this.state.regionValue,
				onCardChange: this.handleRegionCardChange,
				regions: this.state.regions,
				currentRegion: this.state.currentRegion,
				regionOpen: this.state.regionOpen,
				setRegionOpen: this.setRegionOpen,
			}}
			categoryProps={{
				categoryValue: this.state.categoryValue,
				onCardChange: this.handleCategoryCardChange,
				categories: this.state.categories,
				currentCategory: this.state.currentCategory,
				open: this.state.open,
				setOpen: this.setOpen,
			}}
			RegionCardComponent={TranslateRegionCard}
			CategoryCardComponent={TranslateCategoryCard}
			serviceTypeDisplayMode={getServiceTypeDisplayMode(this.state.currentCategory)}
			serviceTypes={this.state.serviceTypesData}
			serviceTypeValue={this.state.serviceTypeValue}
			onServiceTypeChange={this.handleServiceTypeChange}
			facilitiesData={this.state.facilitiesData}
			facilitiesValue={this.state.facilitiesValue}
			onFacilityChange={this.handleFacilityCardChange}
			FacilityCard={FacilityCard}
			dropdownDirection={dropdownDirection}
			layout={layout}
		/>
	);

	renderSourceNote = (className = "service-map-source-note") => {
		const { t } = this.props;
		return (
			<div className={className}>
				{t("serviceMap.sourceNotePrefix")}{" "}
				<a
					href={SERVICE_MAP_SOURCE_URL}
					target="_blank"
					rel="noopener noreferrer"
				>
					{t("serviceMap.sourceLinkLabel")}
				</a>
			</div>
		);
	};

	// Render method to create the filter bar component
	renderFilterBar = (options = {}) => {
		const { showPrintButton = false } = options;
		const { t } = this.props;
		const both = this.props.both;
		const popoverTop = (
			<Popover
				id="popover-positioned-top"
				title="Popover top"
				bsClass="popover-dark"
				className="popover-dark"
			>
				<Popover.Content>
					<div style={{ width: "90%", padding: ".5em" }}>{t("serviceMap.popover")}</div>
					<div
						style={{ width: "10%", marginLeft: "5px" }}
						onClick={this.closePopover}
						class="closingX"
					>
						✕
					</div>
				</Popover.Content>
			</Popover>
		);

		return (
			<div className="filter-bar-container">
				{/* Filter bar moved to top of results area */}
				<Navbar
					onToggle={this.setNavExpanded}
					expanded={this.state.navExpanded}
					defaultExpanded
					bg="light"
					expand="md"
					className="custom-navbar filter-top-navbar"
				>
					{/* Filter header with search results count and clear button */}
					<div className="filter-header">
						<div className="filter-title">
							{t("links.Filters")}
							<span className="search-results">
								{"(" + this.state.filteredLocations.length + t("links.searchResults")}
							</span>
						</div>
						<button 
							className="clearFilters" 
							onClick={this.clearFilter}
						>
							{t("links.ClearFilters")}
						</button>
					</div>
			
					{/* Search input bar */}
					<div className="filter-search-bar d-flex align-items-center">
						<InputGroup className="search-input">
							<FormControl
								placeholder={t("serviceMap.searchPlaceholder")}
								aria-label="Search"
								value={this.state.searchValue}
								onChange={this.handleOrgSearch}
							/>
							<InputGroup.Text>
								<FaSearch />
							</InputGroup.Text>
						</InputGroup>
						{showPrintButton && (
							<div className="ms-2">
								<ReactToPrint
									pageStyle="@page { size: auto; margin: 10mm; } @media print { .wideDiv { grid-template-columns: 1fr !important;} .resourceMap{ width:100vw !important; height:40vh !important; }.resourceTable { padding: 50px !important; width:100vw !important; overflow-y:visible !important;} }"
									trigger={() => (
										<button 
											className="btn btn-secondary d-flex align-items-center justify-content-center print-only-button"
											style={{ marginLeft: '10px' }}
										>
											<img
												src="./print.png"
												width="20px"
												height="20px"
												alt="Print"
											/>
										</button>
									)}
									content={() => this.componentRef}
								/>
							</div>
						)}
					</div>
			
					<div
						className="service-map-filter-hint"
						style={{ marginBottom: "0.75rem", color: "#4f5d75", fontSize: "0.9rem" }}
					>
						{t("serviceMap.defaultSelectionHint")}
					</div>
					{this.renderSourceNote()}

					{/* Filter controls */}
					<Navbar.Collapse id="basic-navbar-nav">
						{this.renderServiceFilterControls("down")}
					</Navbar.Collapse>


					{/* Action buttons for non-both layout */}
					{!both && (
						<div className="filter-actions">
							<OverlayTrigger
								defaultShow={true}
								show={this.state.popoverOpen}
								onClick={this.closePopover}
								placement="top"
								overlay={popoverTop}
							>
								<div
									className={this.props.map ? "cardToggle" : "cardToggle on"}
									onClick={() =>
										this.props.clickNav(false, false, false, false, false, false)
									}
								>
									<img
										id="mapPopover"
										src="./list.png"
										width="17px"
										height="17px"
										alt="List"
									/>
									{t("links.List")}
								</div>
							</OverlayTrigger>
							<div
								className={this.props.map ? "mapToggle on" : "mapToggle"}
								onClick={() =>
									this.props.clickNav(false, true, false, false, false, false)
								}
							>
								<img
									src="./map.png"
									width="17px"
									height="17px"
									alt="Map"
								/>
								{t("links.MapToggle")}
							</div>
							{!this.props.map && (
								<ReactToPrint
									pageStyle="@page { size: auto; margin: 10mm; } @media print { .resourceMap{  }.resourceTable { padding: 10px !important; width:100% !important; overflow-y:visible !important;} }"
									trigger={() => (
										<div className="printToggle">
											<img src="./print.svg" className="print-icon" alt="Print" />
											{t("links.Print")}
										</div>
									)}
									content={() => this.componentRef}
								/>
							)}
						</div>
					)}
				</Navbar>
			</div>
		);
	};

	render() {
		const map = this.props.map;
		const both = this.props.both;
		const { t } = this.props;
		// 检测是否为移动竖屏设备（可以根据实际需求调整判断条件）
		const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth;
		const lowerBarClass = this.state.navExpanded ? "lower-bar" : "lower-bar-close";
		const togglesClass = this.state.navExpanded ? "toggles-margin-top" : "toggles";

		// 底部可折叠Filter Bar（移动竖屏专用）
		const renderMobileBottomFilter = () => (
			<Navbar
				onToggle={this.setNavExpanded}
				expanded={this.state.navExpanded}
				defaultExpanded
				bg="light"
				expand="md"
				fixed="bottom"
				className="custom-navbar mobile-bottom-filter"
			>
				{this.state.navExpanded ? (
					both ? (
						<>
							<div className="top-bar">
								{t("links.Filters")}
								<div className="search-results">
									{" (" + this.state.filteredLocations.length + t("links.searchResults") + ")"}
								</div>
								<div className="clearFilters" onClick={this.clearFilter}>
									{t("links.ClearFilters")}
								</div>
							</div>
							<div className="filter-bar mt-2 mb-2">
								<InputGroup className="search-input">
									<FormControl
										placeholder={t("serviceMap.searchPlaceholder")}
										aria-label="Search"
										value={this.state.searchValue}
										onChange={this.handleOrgSearch}
									/>
									<InputGroup.Text>
										<FaSearch />
									</InputGroup.Text>
								</InputGroup>
							</div>
						</>
					) : (
						<div style={{ width: "100%", float: "left" }}>
							<div style={{ float: "left" }}>
								{t("links.Filters")}
								{"  (" + this.state.filteredLocations.length + t("links.searchResults") + ")"}
							</div>
							<div className="clearFilters" onClick={this.clearFilter}>
								{t("links.ClearFilters")}
							</div>
						</div>
					)
				) : (
					<div className="filter-collapsed">
						<Navbar.Toggle
							aria-controls="basic-navbar-nav"
							className="filter-toggle"
						>
							<div className="filter-words"> {t("links.Filters")}</div>
							<img
								src="./filter.png"
								style={{ marginLeft: "10px" }}
								width="15px"
								height="15px"
								alt="Filter"
							/>
						</Navbar.Toggle>
						<div className="hiddenWhenNotCollapse">
							{"(" + this.state.filteredLocations.length + t("links.searchResults") + ")"}
						</div>
					</div>
				)}

				<div className={lowerBarClass}>
					<Navbar.Collapse id="basic-navbar-nav">
						{this.renderServiceFilterControls("up")}
					</Navbar.Collapse>

					{both ? (
						<div>
							<Nav.Item>
								<ReactToPrint
									pageStyle="@page { size: auto; margin: 10mm; } @media print { .wideDiv { grid-template-columns: 1fr !important;} .resourceMap{ width:100vw !important; height:40vh !important; }.resourceTable { padding: 50px !important; width:100vw !important; overflow-y:visible !important;} }"
									trigger={() => (
										<button
											type="button"
											className="nav-link btn btn-link border-0 bg-transparent p-1"
											aria-label={t("links.Print")}
										>
											<img
												src="./print.png"
												width="25px"
												height="25px"
												alt="Print"
											/>
										</button>
									)}
									content={() => this.componentRef}
								/>
							</Nav.Item>
						</div>
					) : (
						<div className={togglesClass}>
							<OverlayTrigger
								defaultShow={true}
								show={this.state.popoverOpen}
								onClick={this.closePopover}
								placement="top"
								overlay={renderPopoverTop()}
							>
								<div
									className={map ? "cardToggle" : "cardToggle on"}
									onClick={() =>
										this.props.clickNav(false, false, false, false, false, false)
									}
								>
									<img
										id="mapPopover"
										src="./list.png"
										style={{ marginLeft: "10px" }}
										width="17px"
										height="17px"
										alt="List"
									/>
									{t("links.List")}
								</div>
							</OverlayTrigger>
							<div
								className={map ? "mapToggle on" : "mapToggle"}
								onClick={() =>
									this.props.clickNav(false, true, false, false, false, false)
								}
							>
								<img
									src="./map.png"
									style={{ marginLeft: "10px" }}
									width="17px"
									height="17px"
									alt="Map"
								/>
								{t("links.MapToggle")}
							</div>
							{!this.props.map && (
								<ReactToPrint
									pageStyle="@page { size: auto; margin: 10mm; } @media print { .resourceMap{  }.resourceTable { padding: 10px !important; width:100% !important; overflow-y:visible !important;} }"
									trigger={() => (
										<div className="printToggle">
											<img src="./print.svg" className="print-icon" alt="Print" />
											{t("links.Print")}
										</div>
									)}
									content={() => this.componentRef}
								/>
							)}
							{this.state.navExpanded && (
								<Navbar.Toggle
									aria-controls="basic-navbar-nav"
									style={{ float: "right" }}
									onClick={() => this.setNavExpanded(false)}
								>
									{t("links.Done")}
								</Navbar.Toggle>
							)}
						</div>
					)}
				</div>
			</Navbar>
		);

		// 提取PopoverTop为单独方法便于复用
		const renderPopoverTop = () => (
			<Popover
				id="popover-positioned-top"
				title="Popover top"
				bsClass="popover-dark"
				className="popover-dark"
			>
				<Popover.Content>
					<div style={{ width: "90%", padding: ".5em" }}>{t("serviceMap.popover")}</div>
					<div
						style={{ width: "10%", marginLeft: "5px" }}
						onClick={this.closePopover}
						className="closingX"
					>
						✕
					</div>
				</Popover.Content>
			</Popover>
		);

		return (
			<div>
				{this.state.loading || this.state.loadingServices ? (
					<Loader />
				) : (
					<div>
						{/* Welcome Modal */}
						{!this.state.loading && this.state.showWelcomeModal && (
							<div className="welcome-modal-overlay">
								{/* 保持欢迎模态框代码不变 */}
								<div className="welcome-modal">
									<div className="welcome-modal-content">
										<h2 className="welcome-modal-title1">
											{t("serviceMap.welcomeModal.title1")}
										</h2>
										<h2 className="welcome-modal-title2">
											{t("serviceMap.welcomeModal.title2")}
										</h2>
										<p>{t("serviceMap.welcomeModal.description")}</p>
										<p style={{ marginTop: "0.5rem", color: "#4f5d75" }}>
											{t("serviceMap.defaultSelectionHint")}
										</p>
										{this.renderSourceNote("service-map-source-note service-map-source-note--welcome")}
										<div className="welcome-filters service-welcome-filters">
											{this.renderServiceFilterControls("down", "welcome")}
										</div>
										<div className="welcome-modal-actions">
											<button
												className="welcome-modal-btn"
												onClick={this.handleWelcomeModalOkay}
											>
												{t("serviceMap.welcomeModal.okay")}
											</button>
										</div>
									</div>
								</div>
							</div>
						)}

						{/* 主布局 */}
						{both ? (
							<div className="wideDiv" ref={(el) => (this.componentRef = el)}>
								<ResourceMap
									key={`map-${this.state.lat}-${this.state.lng}-${this.state.zoom}`}
									closeFilter={this.doNothing}
									locations={this.state.filteredLocations}
									lat={this.state.lat}
									lng={this.state.lng}
									zoom={this.state.zoom}
									isResource={false}
								/>
								<div className="results-section">
									{/* 横屏和电脑版显示顶部Filter Bar */}
									{!isMobilePortrait && this.renderFilterBar({ showPrintButton: true })}
									<ResourceCard
										callbackFromParent={this.myCallback}
										locations={this.state.filteredLocations}
										isResource={false}
									/>
								</div>
							</div>
						) : map ? (
							<ResourceMap
								key={`map-${this.state.lat}-${this.state.lng}-${this.state.zoom}`}
								closeFilter={this.closeNav}
								onFocus={this.state.showAlert}
								locations={this.state.filteredLocations}
								lat={this.state.lat}
								lng={this.state.lng}
								zoom={this.state.zoom}
								isResource={false}
								ref={(el) => (this.componentRef = el)}
							/>
						) : (
							<div className="single-view-container">
								{/* 横屏和电脑版显示顶部Filter Bar */}
								{!isMobilePortrait && this.renderFilterBar()}
								<ResourceCard
									callbackFromParent={this.myCallback}
									locations={this.state.filteredLocations}
									isResource={false}
									ref={(el) => (this.componentRef = el)}
								/>
							</div>
						)}

						{/* 移动竖屏版显示底部可折叠Filter Bar */}
						{isMobilePortrait && renderMobileBottomFilter()}
					</div>
				)}
			</div>
		);
	}
}

export default translate(ServiceFilterForm);
